<template>
  <form class="ai-composer" :class="{ 'is-focused': focused, 'is-disabled': disabled }" @submit.prevent="submit">
    <label class="sr-only" for="kars-ai-input">{{ $gt('Ihre Frage an KARS-KI') }}</label>
    <textarea
      id="kars-ai-input"
      ref="field"
      v-model="text"
      class="ai-composer__input"
      data-testid="ai-input"
      :disabled="disabled"
      :placeholder="placeholder"
      rows="1"
      @blur="focused = false"
      @focus="focused = true"
      @input="autoGrow"
      @keydown.enter.exact.prevent="submit"
    />
    <button
      v-if="streaming"
      :aria-label="$gt('Antwort stoppen')"
      class="ai-composer__send is-stop"
      data-testid="ai-stop"
      type="button"
      @click="$emit('stop')"
    >
      <Square fill="currentColor" :size="14" />
    </button>
    <button
      v-else
      :aria-label="$gt('Senden')"
      class="ai-composer__send"
      :class="{ 'is-ready': canSend }"
      data-testid="ai-send"
      :disabled="!canSend"
      type="submit"
    >
      <ArrowUp :size="18" />
    </button>
    <p class="ai-composer__hint">
      <span v-if="disabled">{{ $gt('Der Assistent ist derzeit nicht verfügbar.') }}</span>
      <template v-else>
        <kbd>↵</kbd> {{ $gt('Senden') }} · <kbd>⇧↵</kbd> {{ $gt('Zeilenumbruch') }} · <kbd>{{ modKey }}J</kbd> {{ $gt('Öffnen/Schließen') }}
      </template>
    </p>
  </form>
</template>

<script setup lang="ts">
  import { ArrowUp, Square } from 'lucide-vue-next'
  import { computed, nextTick, ref, watch } from 'vue'
  import { gt } from '@/i18n/gt'

  const props = defineProps<{
    streaming: boolean
    disabled: boolean
    /** Vorbelegung von außen (Hilfe-Center, Vorschlags-Chips). */
    prefill?: string | null
  }>()

  const emit = defineEmits<{
    (e: 'send', text: string): void
    (e: 'stop' | 'consumed'): void
  }>()

  const MAX_ROWS = 6
  const text = ref('')
  const focused = ref(false)
  const field = ref<HTMLTextAreaElement | null>(null)

  const canSend = computed(() => text.value.trim().length > 0 && !props.disabled && !props.streaming)
  const placeholder = computed(() => gt('z. B. „Wo stelle ich Benachrichtigungen ein?“'))
  const modKey = computed(() => (typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Strg+'))

  function autoGrow () {
    const el = field.value
    if (!el) return
    el.style.height = 'auto'
    const lineHeight = Number.parseFloat(getComputedStyle(el).lineHeight) || 20
    el.style.height = `${Math.min(el.scrollHeight, lineHeight * MAX_ROWS + 16)}px`
  }

  function submit () {
    if (!canSend.value) return
    emit('send', text.value)
    text.value = ''
    void nextTick(autoGrow)
  }

  function focus () {
    field.value?.focus()
  }

  watch(() => props.prefill, value => {
    if (!value) return
    text.value = value
    emit('consumed')
    void nextTick(() => {
      autoGrow()
      focus()
    })
  }, { immediate: true })

  defineExpose({ focus })
</script>

<style scoped>

  .ai-composer {
    position: relative;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px;
    align-items: end;
    padding: 10px 10px 8px 14px;
    border-radius: 18px;
    border: 1px solid var(--ai-line);
    background: #fff;
    box-shadow: 0 4px 14px rgba(16, 24, 40, 0.06);
    transition: box-shadow 200ms var(--ai-ease), border-color 200ms;
  }

  .ai-composer.is-focused {
    border-color: transparent;
    box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.45), 0 10px 26px rgba(79, 70, 229, 0.14);
  }

  .ai-composer.is-disabled {
    background: var(--ai-surface-2);
  }

  .ai-composer__input {
    width: 100%;
    max-height: 160px;
    padding: 6px 0;
    border: 0;
    outline: 0;
    resize: none;
    background: transparent;
    color: var(--ai-ink);
    font: inherit;
    font-size: 14px;
    line-height: 20px;
  }

  .ai-composer__input::placeholder {
    color: #94a3b8;
  }

  .ai-composer__send {
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    border: 0;
    border-radius: 12px;
    background: var(--ai-surface-2);
    color: #94a3b8;
    cursor: pointer;
    transition: background 200ms, color 200ms, transform 160ms var(--ai-ease), box-shadow 200ms;
  }

  .ai-composer__send.is-ready {
    background: var(--ai-gradient);
    color: #fff;
    box-shadow: 0 8px 18px rgba(79, 70, 229, 0.32);
  }

  .ai-composer__send.is-ready:hover {
    transform: translateY(-1px) scale(1.04);
  }

  .ai-composer__send:disabled {
    cursor: not-allowed;
  }

  .ai-composer__send.is-stop {
    background: var(--ai-ink);
    color: #fff;
  }

  .ai-composer__hint {
    grid-column: 1 / -1;
    margin: 0;
    color: #94a3b8;
    font-size: 11px;
    line-height: 1.4;
  }

  .ai-composer__hint kbd {
    display: inline-block;
    min-width: 16px;
    padding: 0 4px;
    border-radius: 4px;
    border: 1px solid var(--ai-line);
    background: var(--ai-surface-2);
    color: var(--ai-muted);
    font-family: inherit;
    font-size: 10px;
    line-height: 16px;
    text-align: center;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
</style>
